import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { staffCopy } from '../../shared/staff-portal.ts';
import { StaffPhotoView } from './staff-photos.tsx';
import { Alert, LoadingBlock } from '../components/ui.tsx';
import { WorkPage, WorkSection, WorkEmpty, StaffNavigation } from '../components/work-page.tsx';

type Announcement = {
  id: string;
  title: string;
  body: string;
  pinned: number;
  requires_ack: number;
  is_recipient: number;
  read_at: string | null;
  acknowledged_at: string | null;
};
type Post = {
  id: string;
  body: string;
  name: string | null;
  removed_at: string | null;
  reactions: number;
  reacted: number;
  photos?: { id: string }[];
};
type Receipt = { user_id: string; name: string | null; read_at: string | null; acknowledged_at: string | null };
type List<T> = { items: T[]; nextCursor: string | null };

export default function StaffPage() {
  const s = useSession();
  const identity = JSON.stringify([
    s.user?.companyId,
    s.user?.id,
    s.currentLocation?.id,
    s.user?.role,
    s.user?.permissions,
    s.company?.planFeatures,
    s.company?.accessLocked,
    s.impersonatedBy,
  ]);
  const scope = useRef({ identity });
  if (scope.current.identity !== identity) scope.current = { identity };
  const captured = scope.current;
  return <StaffContent key={identity} scopeCurrent={() => scope.current === captured} />;
}

function StaffContent({ scopeCurrent }: { scopeCurrent: () => boolean }) {
  const { i18n } = useTranslation();
  const copy = staffCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const [cap, setCap] = useState<{
    canWrite: boolean;
    canManage: boolean;
    timeOff?: boolean;
    swaps?: boolean;
    photos?: boolean;
  } | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [postBody, setPostBody] = useState('');
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [plan, setPlan] = useState(false);
  const [aCursor, setACursor] = useState<string | null>(null),
    [pCursor, setPCursor] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, Post[]>>({}),
    [commentBody, setCommentBody] = useState<Record<string, string>>({});
  const [report, setReport] = useState<Receipt[] | null>(null);
  const alive = useRef(true),
    generation = useRef(0),
    lock = useRef(false);
  const pending = useRef<{ path: string; body: unknown; done?: () => void } | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const current = () => alive.current && scopeCurrent();
  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, []);
  async function refresh() {
    const gen = ++generation.current;
    setError('');
    try {
      const [cap, a, p] = await Promise.all([
        api.get<{
          canWrite: boolean;
          canManage: boolean;
          timeOff?: boolean;
          swaps?: boolean;
          photos?: boolean;
        }>('/staff/capability'),
        api.get<List<Announcement>>('/staff/announcements'),
        api.get<List<Post>>('/staff/posts'),
      ]);
      if (!current() || gen !== generation.current) return;
      setCap(cap);
      setAnnouncements(a.items);
      setPosts(p.items);
      setACursor(a.nextCursor);
      setPCursor(p.nextCursor);
    } catch (e) {
      if (!current() || gen !== generation.current) return;
      const status = (e as { status?: number }).status;
      if (status && [401, 402, 403, 404, 410].includes(status)) {
        // Retire callbacks started while this access check was in flight.
        generation.current++;
        setCap(null);
        setAnnouncements([]);
        setPosts([]);
        setReport(null);
        setComments({});
        setCommentBody({});
        setPostBody('');
        setComposing(false);
        setACursor(null);
        setPCursor(null);
        pending.current = null;
        setUncertain(false);
      }
      if ((e as { status?: number; code?: string }).status === 402 || (e as { code?: string }).code === 'PLAN_REQUIRED')
        setPlan(true);
      else setError(copy.failed);
    }
  }
  async function mutate(path: string, data: unknown, done?: () => void) {
    if (!current() || lock.current) return;
    if (!navigator.onLine) {
      setError(copy.offline);
      return;
    }
    if (pending.current && pending.current.path !== path) return;
    lock.current = true;
    setBusy(true);
    setError('');
    pending.current ??= { path, body: data, done };
    const gen = generation.current;
    try {
      await api.post(pending.current.path, pending.current.body);
      if (!current() || gen !== generation.current) return;
      const completed = pending.current.done;
      pending.current = null;
      setUncertain(false);
      completed?.();
      await refresh();
    } catch (error) {
      if (current() && gen === generation.current) {
        const status = (error as { status?: number }).status;
        const rejected = status !== undefined && status >= 400 && status < 500 && status !== 408;
        if (rejected) pending.current = null;
        setUncertain(!rejected);
        setError(copy.failed);
      }
    } finally {
      lock.current = false;
      if (current()) setBusy(false);
    }
  }
  async function more(kind: 'announcements' | 'posts') {
    const cursor = kind === 'announcements' ? aCursor : pCursor;
    if (!cursor || lock.current) return;
    const gen = generation.current;
    lock.current = true;
    setBusy(true);
    try {
      const page = await api.get<List<Announcement> | List<Post>>(
        `/staff/${kind}?before=${encodeURIComponent(cursor)}`,
      );
      if (!current() || gen !== generation.current) return;
      if (kind === 'announcements') {
        setAnnouncements((old) => [...old, ...(page.items as Announcement[])]);
        setACursor(page.nextCursor);
      } else {
        setPosts((old) => [...old, ...(page.items as Post[])]);
        setPCursor(page.nextCursor);
      }
    } catch {
      if (current()) setError(copy.failed);
    } finally {
      lock.current = false;
      if (current()) setBusy(false);
    }
  }
  async function viewComments(id: string) {
    const gen = generation.current;
    try {
      const result = await api.get<List<Post>>(`/staff/posts?parentId=${id}`);
      if (current() && gen === generation.current) setComments((old) => ({ ...old, [id]: result.items }));
    } catch {
      if (current()) setError(copy.failed);
    }
  }
  const button =
    'inline-flex min-h-11 items-center justify-center rounded border px-3 py-2 text-center disabled:opacity-50';
  const field = 'block w-full rounded border p-2';
  const disabled = busy || uncertain;
  return (
    <WorkPage
      title={copy.title}
      description={
        i18n.language.startsWith('es')
          ? 'Anuncios, novedades del equipo, documentos y tiempo libre de su ubicación.'
          : 'Announcements, team updates, documents and time off for your location.'
      }
      actions={
        <button className="btn btn-secondary" onClick={() => void refresh()} disabled={busy}>
          {copy.refresh}
        </button>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      {plan && <Alert>{copy.plan}</Alert>}
      {!cap && !error && !plan && (
        <div className="card">
          <LoadingBlock />
        </div>
      )}
      {uncertain && (
        <button
          className={button}
          disabled={busy}
          onClick={() => pending.current && void mutate(pending.current.path, pending.current.body)}
        >
          {copy.retry}
        </button>
      )}
      {cap && (
        <>
          <div className="flex flex-wrap items-center gap-3" aria-label={copy.actions}>
            {cap.canManage ? (
              <a className="btn btn-primary min-h-11" href="/staff/manage">
                {copy.newMessage}
              </a>
            ) : (
              cap.canWrite && (
                <button
                  className="btn btn-primary min-h-11"
                  aria-expanded={composing}
                  onClick={() => setComposing(true)}
                >
                  {copy.newMessage}
                </button>
              )
            )}
            {cap.timeOff && (
              <a className={button} href="/staff/time-off">
                {copy.requestTimeOff}
              </a>
            )}
            {cap.swaps && (
              <a className={button} href="/staff/swaps">
                {copy.swaps}
              </a>
            )}
          </div>
          <StaffNavigation active="updates" canManage={cap.canManage} ownDocuments={!cap.canManage} />
          {cap.canWrite && composing && (
            <form
              className="space-y-3 rounded border p-4"
              onSubmit={(e) => {
                e.preventDefault();
                void mutate('/staff/posts', { clientMutationId: crypto.randomUUID(), body: postBody }, () => {
                  setPostBody('');
                  setComposing(false);
                });
              }}
            >
              <p className="font-semibold">{copy.sendToEveryone}</p>
              <label className="block">
                {copy.body}
                <textarea
                  className={field}
                  autoFocus
                  rows={4}
                  required
                  maxLength={4000}
                  value={postBody}
                  disabled={disabled}
                  onChange={(e) => setPostBody(e.target.value)}
                />
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <button className="btn btn-primary min-h-11" disabled={disabled || !postBody.trim()}>
                  {copy.send}
                </button>
                <button type="button" className={button} disabled={disabled} onClick={() => setComposing(false)}>
                  {copy.close}
                </button>
              </div>
            </form>
          )}
          <WorkSection title={copy.announcements}>
            {!announcements.length && (
              <WorkEmpty title={copy.empty}>
                <p>
                  {i18n.language.startsWith('es')
                    ? 'Los mensajes y avisos de su ubicación aparecerán aquí.'
                    : 'Messages and notices for your location will appear here.'}
                </p>
              </WorkEmpty>
            )}
            {[...announcements]
              .sort((a, b) => b.pinned - a.pinned)
              .map((a) => (
                <article key={a.id} className="work-row space-y-3">
                  <h3 className="font-bold">
                    {a.pinned ? '📌 ' : ''}
                    {a.title}
                  </h3>
                  <p className="whitespace-pre-wrap break-words">{a.body}</p>
                  <p>
                    {a.read_at ? copy.readStatus : copy.unread}
                    {a.requires_ack ? ` · ${a.acknowledged_at ? copy.ackStatus : copy.pendingAck}` : ''}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={button}
                      disabled={disabled || a.is_recipient === 0 || !!a.read_at}
                      onClick={() => void mutate(`/staff/announcements/${a.id}/read`, {})}
                    >
                      {copy.read}
                    </button>
                    {a.requires_ack && (
                      <button
                        className={button}
                        disabled={disabled || a.is_recipient === 0 || !!a.acknowledged_at}
                        onClick={() => void mutate(`/staff/announcements/${a.id}/ack`, {})}
                      >
                        {copy.ack}
                      </button>
                    )}
                    {cap.canManage && (
                      <button
                        className={button}
                        onClick={async () => {
                          const gen = generation.current;
                          try {
                            const r = await api.get<{ items: Receipt[] }>(`/staff/announcements/${a.id}/report`);
                            if (current() && gen === generation.current) setReport(r.items);
                          } catch {
                            if (current()) setError(copy.failed);
                          }
                        }}
                      >
                        {copy.report}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            {aCursor && (
              <button className={button} disabled={busy} onClick={() => void more('announcements')}>
                {copy.more}
              </button>
            )}
            {report && (
              <div>
                <h3 className="font-semibold">{copy.report}</h3>
                {report.map((r) => (
                  <p key={r.user_id}>
                    {r.name ?? copy.former}: {r.read_at ? copy.readStatus : copy.unread},{' '}
                    {r.acknowledged_at ? copy.ackStatus : copy.pendingAck}
                  </p>
                ))}
              </div>
            )}
          </WorkSection>
          <section className="card work-section space-y-3" aria-label={copy.feed}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">{copy.feed}</h2>
              <div className="flex flex-wrap gap-3">
                {cap.canWrite && (
                  <button className={button} aria-expanded={composing} onClick={() => setComposing(true)}>
                    {copy.shareUpdate}
                  </button>
                )}
                {cap.canWrite && cap.photos && (
                  <button
                    type="button"
                    className={button}
                    disabled={disabled}
                    onClick={() => {
                      if (lock.current || pending.current) return;
                      if (postBody && !window.confirm(copy.discardForPhotos)) return;
                      setPostBody('');
                      setComposing(false);
                      window.location.assign('/staff/photos');
                    }}
                  >
                    {copy.addPhotos}
                  </button>
                )}
              </div>
            </div>
            {!posts.length && (
              <WorkEmpty title={copy.empty}>
                <p>
                  {i18n.language.startsWith('es')
                    ? 'Las novedades compartidas por su equipo aparecerán aquí.'
                    : 'Updates shared by your team will appear here.'}
                </p>
              </WorkEmpty>
            )}
            {posts.map((p) => (
              <article className="work-row space-y-3" key={p.id}>
                <p className="font-bold">{p.name ?? copy.former}</p>
                <p className="whitespace-pre-wrap break-words">{p.removed_at ? copy.removed : p.body}</p>
                {!p.removed_at && p.photos?.map((photo) => <StaffPhotoView key={photo.id} id={photo.id} />)}
                {!p.removed_at && (
                  <div className="flex flex-wrap gap-2">
                    {cap.canWrite && (
                      <button
                        className={button}
                        aria-pressed={!!p.reacted}
                        disabled={disabled}
                        onClick={() => void mutate(`/staff/posts/${p.id}/reaction`, { liked: !p.reacted })}
                      >
                        {copy.reaction} ({p.reactions})
                      </button>
                    )}
                    <button className={button} onClick={() => void viewComments(p.id)}>
                      {copy.comment}
                    </button>
                    {cap.canManage && (
                      <button
                        className={button}
                        disabled={disabled}
                        onClick={() => void mutate(`/staff/posts/${p.id}/remove`, {})}
                      >
                        {copy.remove}
                      </button>
                    )}
                  </div>
                )}
                {comments[p.id]?.map((c) => (
                  <p className="break-words border-l pl-3" key={c.id}>
                    {c.name ?? copy.former}: {c.removed_at ? copy.removed : c.body}
                  </p>
                ))}
                {cap.canWrite && !p.removed_at && comments[p.id] && (
                  <form
                    className="flex flex-wrap gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void mutate(
                        '/staff/posts',
                        { clientMutationId: crypto.randomUUID(), body: commentBody[p.id], parentId: p.id },
                        () => {
                          setCommentBody((old) => ({ ...old, [p.id]: '' }));
                          void viewComments(p.id);
                        },
                      );
                    }}
                  >
                    <label className="min-w-0 flex-1">
                      {copy.comment}
                      <input
                        required
                        className={field}
                        maxLength={4000}
                        disabled={disabled}
                        value={commentBody[p.id] ?? ''}
                        onChange={(e) => setCommentBody((old) => ({ ...old, [p.id]: e.target.value }))}
                      />
                    </label>
                    <button className={button} disabled={disabled}>
                      {copy.post}
                    </button>
                  </form>
                )}
              </article>
            ))}
            {pCursor && (
              <button className={button} disabled={busy} onClick={() => void more('posts')}>
                {copy.more}
              </button>
            )}
          </section>
        </>
      )}
    </WorkPage>
  );
}
